/**
 * Talking to the API.
 *
 * Deliberately thin. No client library, no cache layer — the device list polls
 * one endpoint and the login form posts to another, and anything more would be
 * machinery for a screen that gets rewritten when live view lands.
 */

const TOKEN_KEY = 'jeprotect.session';

export interface ApiCamera {
  id: string;
  name: string | null;
  state: string;
}

export interface ApiDevice {
  id: string;
  name: string;
  status: string;
  last_seen_at: string | null;
  site_name: string | null;
  cameras: ApiCamera[];
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export function storedToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private browsing, or storage disabled. Treated as signed out rather than
    // crashing the app on load.
    return null;
  }
}

export function storeToken(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(TOKEN_KEY);
    else window.localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* Same as above: not being able to persist is not fatal. */
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = storedToken();

  const response = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  const body: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string } } | null)?.error;

    /*
     * A rejected session is dropped immediately, not on the next poll.
     *
     * Sessions are now revocable, so 401 on an authenticated request means the
     * account was removed, its role changed, or the token expired — all of which
     * end with signing in again. Keeping the dead token would let every
     * subsequent request fail in a way that reads as the server being broken.
     *
     * Only when a token was actually sent: a 401 from the login form is a wrong
     * password, not a revoked session.
     */
    if (response.status === 401 && token) storeToken(null);

    throw new ApiError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? `Request failed (${response.status})`,
    );
  }

  return body as T;
}

export async function login(email: string, password: string): Promise<string> {
  // The tenant is resolved server-side from the request. There is deliberately
  // no tenant field here, and adding one would let a caller probe which tenants
  // an address exists in.
  const result = await request<{ token: string }>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  storeToken(result.token);
  return result.token;
}

export async function fetchDevices(): Promise<ApiDevice[]> {
  const result = await request<{ devices: ApiDevice[] }>('/api/devices');
  return result.devices;
}

export interface ApiSite {
  id: string;
  name: string;
  address: string | null;
  timezone: string | null;
  client_id: string | null;
  client_name: string | null;
  device_count: number;
}

export interface ApiClient {
  id: string;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string | null;
  notes: string | null;
  site_count: number;
  device_count: number;
}

export interface ClientInput {
  name: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  country?: string;
  notes?: string;
}

export async function fetchClients(): Promise<ApiClient[]> {
  const result = await request<{ clients: ApiClient[] }>('/api/clients');
  return result.clients;
}

export async function createClient(input: ClientInput): Promise<{ id: string; name: string }> {
  const result = await request<{ client: { id: string; name: string } }>('/api/clients', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return result.client;
}

export async function deleteClient(id: string): Promise<void> {
  await request(`/api/clients/${id}`, { method: 'DELETE' });
}

export async function deleteSite(id: string): Promise<void> {
  await request(`/api/sites/${id}`, { method: 'DELETE' });
}

/**
 * What a deletion would destroy.
 *
 * Read before the delete so the confirmation can name the consequence. A dialog
 * that says "delete?" is a keystroke between an operator and a client's recorded
 * history; one that says "18 days across 14 cameras" is a different question.
 */
export interface DeletionImpact {
  label: string;
  cameras: number;
  segments: number;
  oldestSegmentAt: number | null;
  newestSegmentAt: number | null;
  historyDays: number;
  devices: number;
}

export async function fetchDeletionImpact(
  type: 'cameras' | 'devices' | 'sites',
  id: string,
): Promise<DeletionImpact> {
  const result = await request<{ impact: DeletionImpact }>(`/api/${type}/${id}/impact`);
  return result.impact;
}

/** The device name must be typed to confirm. The server checks it too. */
export async function deleteDevice(
  id: string,
  confirmName: string,
): Promise<{ removedCameras: number }> {
  return request(`/api/devices/${id}`, {
    method: 'DELETE',
    body: JSON.stringify({ confirmName }),
  });
}

export interface AuditEvent {
  id: string;
  actor_user_id: string | null;
  actor_email: string | null;
  actor_kind: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  target_label: string | null;
  detail: Record<string, unknown> | null;
  source_ip: string | null;
  occurred_at: string;
}

export async function fetchAudit(params: { before?: string; action?: string } = {}): Promise<AuditEvent[]> {
  const query = new URLSearchParams();
  if (params.before) query.set('before', params.before);
  if (params.action) query.set('action', params.action);

  const result = await request<{ events: AuditEvent[] }>(
    `/api/audit${query.toString() ? `?${query.toString()}` : ''}`,
  );
  return result.events;
}

export async function deleteCamera(id: string): Promise<void> {
  await request(`/api/cameras/${id}`, { method: 'DELETE' });
}

export async function fetchSites(): Promise<ApiSite[]> {
  const result = await request<{ sites: ApiSite[] }>('/api/sites');
  return result.sites;
}

export async function createSite(input: {
  clientId: string;
  name: string;
  address?: string;
  timezone?: string;
}): Promise<ApiSite> {
  const result = await request<{ site: ApiSite }>('/api/sites', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return result.site;
}

export interface ProvisionedDevice {
  device: { id: string; name: string };
  /** Returned exactly once, by this response. Never stored, never recoverable. */
  claimCode: string;
  claimCodeExpiresAt: string;
}

export async function provisionDevice(input: {
  siteId: string;
  name: string;
  maxCameras: number;
}): Promise<ProvisionedDevice> {
  return request<ProvisionedDevice>('/api/devices', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Reissues a claim code. The only recovery when one is lost or burnt out. */
export async function reissueClaimCode(deviceId: string): Promise<{
  claimCode: string;
  claimCodeExpiresAt: string;
}> {
  return request(`/api/devices/${deviceId}/claim-code`, { method: 'POST' });
}

export interface ReprovisionResult {
  revoked: number;
  disconnected: boolean;
  claimCode: string;
  claimCodeExpiresAt: string;
}

/**
 * Revokes every token, issues a fresh claim code, and hangs up on the device.
 *
 * Destructive: the appliance in the field stops being able to reach the cloud
 * until someone re-claims it. Used when a device is replaced, factory reset, or
 * suspected compromised.
 */
export async function reprovisionDevice(
  deviceId: string,
  reason: 'operator_revoked' | 'decommissioned' | 'suspected_compromise',
): Promise<ReprovisionResult> {
  return request(`/api/devices/${deviceId}/reprovision`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/**
 * Ciphertext plus the fingerprint of the key it was sealed to.
 *
 * The fingerprint is what lets the server refuse a credential sealed to a stale
 * key, instead of storing something the device can never open.
 */
export interface SealedCredentials {
  sealed: string;
  keyFingerprint: string;
}

export async function addCamera(input: {
  deviceId: string;
  name?: string | undefined;
  onvifHost: string;
  subProfileUri: string;
  mainProfileUri?: string | undefined;
  credentials?: SealedCredentials | undefined;
}): Promise<{ camera: { id: string } }> {
  return request('/api/cameras', { method: 'POST', body: JSON.stringify(input) });
}

export type { CameraDetail, DeviceDetailData } from './deviceDetail.ts';

/** The detail endpoint's shape. Defined with the view model that consumes it. */
export type ApiDeviceDetail = import('./deviceDetail.ts').DeviceDetailData;

export async function fetchDevice(id: string): Promise<ApiDeviceDetail> {
  const result = await request<{ device: ApiDeviceDetail }>(`/api/devices/${id}`);
  return result.device;
}

/**
 * Edits a camera. Every field optional; only what is sent is written.
 *
 * Credentials are deliberately not here — they go through their own endpoint,
 * because they are the one field the server cannot read back and has to validate
 * against the device key. Mixing them in would mean a rename could be refused for
 * a stale key.
 */
export async function updateCamera(
  id: string,
  changes: {
    name?: string | null;
    onvifHost?: string;
    subProfileUri?: string;
    mainProfileUri?: string | null;
    recordingEnabled?: boolean;
  },
): Promise<void> {
  await request(`/api/cameras/${id}`, { method: 'PATCH', body: JSON.stringify(changes) });
}

/**
 * Replaces a camera's credentials.
 *
 * A rotation, not a delete-and-re-add: cameras get their passwords changed, and
 * that must not cost the camera its recording history.
 */
export async function updateCameraCredentials(
  cameraId: string,
  credentials: SealedCredentials,
): Promise<void> {
  await request(`/api/cameras/${cameraId}/credentials`, {
    method: 'PUT',
    body: JSON.stringify(credentials),
  });
}


// ------------------------------------------------------------------- users

export interface ApiUser {
  id: string;
  email: string;
  role: 'viewer' | 'installer' | 'operator' | 'owner';
  last_login_at: string | null;
  created_at: string;
  /** True while the account has no password — an invitation nobody has accepted. */
  pending: boolean;
  invite_expires_at: string | null;
  /** 'all' sees the whole tenant; 'selected' sees only granted sites. */
  access_scope: 'all' | 'selected';
  site_ids: string[];
  excluded_camera_ids: string[];
}

/**
 * Replaces a user's access.
 *
 * A whole replacement rather than add/remove, because access is a set — two
 * endpoints mutating it independently is how a half-applied change leaves
 * someone seeing a client they were never meant to.
 */
export async function setUserAccess(
  id: string,
  access: { scope: 'all' | 'selected'; siteIds: string[]; excludedCameraIds: string[] },
): Promise<void> {
  await request(`/api/users/${id}/access`, { method: 'PUT', body: JSON.stringify(access) });
}

export interface IssuedInvite {
  inviteCode: string;
  inviteExpiresAt: string;
  email: string;
}

export async function fetchUsers(): Promise<ApiUser[]> {
  const result = await request<{ users: ApiUser[] }>('/api/users');
  return result.users;
}

export async function inviteUser(email: string, role: ApiUser['role']): Promise<IssuedInvite> {
  const result = await request<{ user: ApiUser; inviteCode: string; inviteExpiresAt: string }>(
    '/api/users',
    { method: 'POST', body: JSON.stringify({ email, role }) },
  );
  return {
    inviteCode: result.inviteCode,
    inviteExpiresAt: result.inviteExpiresAt,
    email: result.user.email,
  };
}

/** Reissues an invitation. The only recovery when a link is lost or burnt out. */
export async function reinviteUser(id: string): Promise<IssuedInvite> {
  return request<IssuedInvite>(`/api/users/${id}/invite`, { method: 'POST' });
}

export async function setUserRole(id: string, role: ApiUser['role']): Promise<void> {
  await request(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify({ role }) });
}

export async function removeUser(id: string): Promise<void> {
  await request(`/api/users/${id}`, { method: 'DELETE' });
}

/**
 * Accepting an invitation.
 *
 * The only call in this file that deliberately sends no session token — there is
 * no session yet, and having one is what this creates the means to obtain.
 */
export async function acceptInvite(inviteCode: string, password: string): Promise<{ email: string }> {
  const response = await fetch('/api/users/accept-invite', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ inviteCode, password }),
  });

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (body as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? `Request failed (${response.status})`,
    );
  }
  return body as { email: string };
}

export async function updateEntitlement(
  deviceId: string,
  changes: { maxCameras?: number; cloudEventRetentionDays?: number; expiresAt?: string | null },
): Promise<void> {
  await request(`/api/devices/${deviceId}/entitlement`, {
    method: 'PATCH',
    body: JSON.stringify(changes),
  });
}


// --------------------------------------------------------------- discovery

export interface DiscoveredCamera {
  address: string;
  serviceUrl: string;
  name: string | null;
  manufacturer: string | null;
  model: string | null;
  urn: string | null;
  /** Already configured on this device, so adding it again would duplicate it. */
  alreadyAdded: boolean;
}

export interface ProbeResult {
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  firmwareVersion: string | null;
  subProfileUri: string;
  mainProfileUri: string | null;
  profiles: Array<{
    token: string;
    name: string | null;
    width: number | null;
    height: number | null;
    encoding: string | null;
  }>;
}

/**
 * Asks the device to scan its LAN.
 *
 * Slow by nature — the device listens for several seconds — so the caller must
 * show that something is happening rather than appearing to hang.
 */
export async function scanForCameras(deviceId: string): Promise<{ cameras: DiscoveredCamera[] }> {
  return request(`/api/devices/${deviceId}/scan`, { method: 'POST' });
}

/**
 * Asks the device to interrogate one camera with a sealed credential.
 *
 * The credential is relayed, not stored. If the operator then adds the camera it
 * is sealed again for the row — this call is a question, not a configuration
 * change.
 */
export async function probeCamera(
  deviceId: string,
  serviceUrl: string,
  credentials: SealedCredentials,
): Promise<ProbeResult> {
  return request(`/api/devices/${deviceId}/probe`, {
    method: 'POST',
    body: JSON.stringify({ serviceUrl, credentials }),
  });
}
